import React, { useState, useEffect } from 'react';
import { trucksAPI, driversAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const TruckManagement = () => {
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTruck, setEditingTruck] = useState(null);
  const [formData, setFormData] = useState({
    truck_number: '',
    vin: '',
    make: '',
    model: '',
    year: '',
    license_plate: '',
    registration_expiry: '',
    insurance_expiry: '',
    capacity_tons: '',
    length_feet: '',
    width_feet: '',
    height_feet: '',
    status: 'available',
    current_driver_id: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [trucksData, driversData] = await Promise.all([
        trucksAPI.getAll(),
        driversAPI.getAll(),
      ]);
      setTrucks(trucksData);
      setDrivers(driversData);
    } catch (error) {
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        capacity_tons: formData.capacity_tons ? parseFloat(formData.capacity_tons) : null,
        length_feet: formData.length_feet ? parseFloat(formData.length_feet) : null,
        width_feet: formData.width_feet ? parseFloat(formData.width_feet) : null,
        height_feet: formData.height_feet ? parseFloat(formData.height_feet) : null,
        current_driver_id: formData.current_driver_id ? parseInt(formData.current_driver_id) : null,
      };
      if (editingTruck) {
        await trucksAPI.update(editingTruck.id, submitData);
      } else {
        await trucksAPI.create(submitData);
      }
      setShowModal(false);
      setEditingTruck(null);
      resetForm();
      loadData();
    } catch (error) {
      alert('Error saving truck: ' + error.message);
    }
  };

  const handleEdit = (truck) => {
    setEditingTruck(truck);
    setFormData({
      truck_number: truck.truck_number || '',
      vin: truck.vin || '',
      make: truck.make || '',
      model: truck.model || '',
      year: truck.year || '',
      license_plate: truck.license_plate || '',
      registration_expiry: truck.registration_expiry || '',
      insurance_expiry: truck.insurance_expiry || '',
      capacity_tons: truck.capacity_tons || '',
      length_feet: truck.length_feet || '',
      width_feet: truck.width_feet || '',
      height_feet: truck.height_feet || '',
      status: truck.status || 'available',
      current_driver_id: truck.current_driver_id || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this truck?')) return;
    try {
      await trucksAPI.delete(id);
      loadData();
    } catch (error) {
      alert('Error deleting truck: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      truck_number: '',
      vin: '',
      make: '',
      model: '',
      year: '',
      license_plate: '',
      registration_expiry: '',
      insurance_expiry: '',
      capacity_tons: '',
      length_feet: '',
      width_feet: '',
      height_feet: '',
      status: 'available',
      current_driver_id: '',
    });
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'truck_number', label: 'Truck #' },
    { key: 'make', label: 'Make' },
    { key: 'model', label: 'Model' },
    { key: 'year', label: 'Year' },
    { key: 'license_plate', label: 'License Plate' },
    { key: 'capacity_tons', label: 'Capacity (tons)' },
    { key: 'length_feet', label: 'Length (ft)' },
    { key: 'width_feet', label: 'Width (ft)' },
    { key: 'height_feet', label: 'Height (ft)' },
    { key: 'status', label: 'Status' },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Truck Management</h1>
        <button className="btn-primary" onClick={() => { setEditingTruck(null); resetForm(); setShowModal(true); }}>
          Add New Truck
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={trucks}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingTruck(null); resetForm(); }}
        title={editingTruck ? 'Edit Truck' : 'Add New Truck'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Truck Number *</label>
            <input
              type="text"
              value={formData.truck_number}
              onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>VIN</label>
              <input
                type="text"
                value={formData.vin}
                onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>License Plate</label>
              <input
                type="text"
                value={formData.license_plate}
                onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Make</label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Model</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Year</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Registration Expiry</label>
              <input
                type="date"
                value={formData.registration_expiry}
                onChange={(e) => setFormData({ ...formData, registration_expiry: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Insurance Expiry</label>
              <input
                type="date"
                value={formData.insurance_expiry}
                onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Capacity (tons)</label>
              <input
                type="number"
                step="0.01"
                value={formData.capacity_tons}
                onChange={(e) => setFormData({ ...formData, capacity_tons: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Length (ft)</label>
              <input
                type="number"
                step="0.01"
                value={formData.length_feet}
                onChange={(e) => setFormData({ ...formData, length_feet: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Width (ft)</label>
              <input
                type="number"
                step="0.01"
                value={formData.width_feet}
                onChange={(e) => setFormData({ ...formData, width_feet: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Height (ft)</label>
              <input
                type="number"
                step="0.01"
                value={formData.height_feet}
                onChange={(e) => setFormData({ ...formData, height_feet: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
              >
                <option value="available">Available</option>
                <option value="in_transit">In Transit</option>
                <option value="maintenance">Maintenance</option>
                <option value="out_of_service">Out of Service</option>
              </select>
            </div>
            <div className="form-group">
              <label>Current Driver</label>
              <select
                value={formData.current_driver_id}
                onChange={(e) => setFormData({ ...formData, current_driver_id: e.target.value })}
              >
                <option value="">None</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingTruck(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default TruckManagement;


